import { motion } from 'framer-motion';
import { Download, RefreshCw, Trash2, Upload } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '../components/Button';
import { CONTACT_INFO } from '../constants/contact';

interface Template {
  id: string;
  name: string;
  backgroundColor: string;
  gradientColors?: string[];
  textColor: string;
  accentColor?: string;
  style: 'minimal' | 'gradient' | 'highlight';
}

type BlockType =
  | 'heading'
  | 'text'
  | 'separator-line'
  | 'separator-dots'
  | 'next-arrow'
  | 'spacer';

type ArrowStyle =
  | 'simple'
  | 'circle'
  | 'filled-circle'
  | 'squared'
  | 'double-angle';

interface ContentBlock {
  id: string;
  type: BlockType;
  content: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold' | 'light';
  fontStyle?: 'normal' | 'italic';
  alignment?: 'left' | 'center' | 'right';
  fontFamily?: string; // Custom font for this block
  arrowStyle?: ArrowStyle; // Arrow style
  x?: number; // Custom X position (for separators/arrows)
  y?: number; // Custom Y position (for separators/arrows)
}

const TEMPLATES: Template[] = [
  // Warm & Earthy Tones
  {
    id: 'beige-minimal',
    name: 'Beige Minimal',
    backgroundColor: '#E8DCC4',
    textColor: '#1a1a1a',
    style: 'minimal',
  },
  {
    id: 'cream-elegant',
    name: 'Cream Elegant',
    backgroundColor: '#F5E6D3',
    textColor: '#2C2416',
    style: 'minimal',
  },
  {
    id: 'terracotta-warm',
    name: 'Terracotta',
    backgroundColor: '#E07A5F',
    textColor: '#ffffff',
    style: 'minimal',
  },
  {
    id: 'sand-desert',
    name: 'Desert Sand',
    backgroundColor: '#F4A261',
    textColor: '#2C1810',
    style: 'minimal',
  },

  // Pastel & Soft
  {
    id: 'peach-highlight',
    name: 'Peach Highlight',
    backgroundColor: '#FFF4E6',
    textColor: '#1a1a1a',
    accentColor: '#FFD99A',
    style: 'highlight',
  },
  {
    id: 'sage-minimal',
    name: 'Sage Minimal',
    backgroundColor: '#E8EDE7',
    textColor: '#2C3428',
    style: 'minimal',
  },
  {
    id: 'rose-highlight',
    name: 'Rose Highlight',
    backgroundColor: '#FFF0F0',
    textColor: '#2C1A1A',
    accentColor: '#FFD4D4',
    style: 'highlight',
  },
  {
    id: 'lavender-soft',
    name: 'Lavender Dream',
    backgroundColor: '#E8DEEE',
    textColor: '#3A2449',
    style: 'minimal',
  },
  {
    id: 'mint-fresh',
    name: 'Fresh Mint',
    backgroundColor: '#D5F4E6',
    textColor: '#1B4332',
    style: 'minimal',
  },
  {
    id: 'sky-blue',
    name: 'Sky Blue',
    backgroundColor: '#DAEEFF',
    textColor: '#0A2239',
    style: 'minimal',
  },

  // Bold & Vibrant
  {
    id: 'coral-vibrant',
    name: 'Coral Pop',
    backgroundColor: '#FF6B6B',
    textColor: '#ffffff',
    style: 'minimal',
  },
  {
    id: 'teal-bold',
    name: 'Teal Bold',
    backgroundColor: '#2A9D8F',
    textColor: '#ffffff',
    style: 'minimal',
  },
  {
    id: 'purple-royal',
    name: 'Royal Purple',
    backgroundColor: '#7209B7',
    textColor: '#ffffff',
    style: 'minimal',
  },
  {
    id: 'emerald-rich',
    name: 'Emerald',
    backgroundColor: '#2D6A4F',
    textColor: '#ffffff',
    style: 'minimal',
  },

  // Dark & Sophisticated
  {
    id: 'charcoal-dark',
    name: 'Charcoal',
    backgroundColor: '#2B2D42',
    textColor: '#EDF2F4',
    style: 'minimal',
  },
  {
    id: 'navy-deep',
    name: 'Deep Navy',
    backgroundColor: '#1B263B',
    textColor: '#E0E1DD',
    style: 'minimal',
  },
  {
    id: 'black-elegant',
    name: 'Pure Black',
    backgroundColor: '#0D1B2A',
    textColor: '#F8F9FA',
    style: 'minimal',
  },
  {
    id: 'burgundy-rich',
    name: 'Burgundy',
    backgroundColor: '#6A040F',
    textColor: '#F8EDEB',
    style: 'minimal',
  },

  // Gradients - Vibrant
  {
    id: 'ocean-gradient',
    name: 'Ocean Wave',
    gradientColors: ['#667eea', '#764ba2'],
    textColor: '#ffffff',
    style: 'gradient',
    backgroundColor: '#667eea',
  },
  {
    id: 'sunset-gradient',
    name: 'Sunset Glow',
    gradientColors: ['#f093fb', '#f5576c'],
    textColor: '#ffffff',
    style: 'gradient',
    backgroundColor: '#f093fb',
  },
  {
    id: 'forest-gradient',
    name: 'Forest Mist',
    gradientColors: ['#134e5e', '#71b280'],
    textColor: '#ffffff',
    style: 'gradient',
    backgroundColor: '#134e5e',
  },
  {
    id: 'fire-gradient',
    name: 'Fire Blaze',
    gradientColors: ['#ff6e7f', '#bfe9ff'],
    textColor: '#1a1a1a',
    style: 'gradient',
    backgroundColor: '#ff6e7f',
  },
  {
    id: 'purple-dream',
    name: 'Purple Dream',
    gradientColors: ['#a8edea', '#fed6e3'],
    textColor: '#2a2a72',
    style: 'gradient',
    backgroundColor: '#a8edea',
  },
  {
    id: 'orange-coral',
    name: 'Orange Coral',
    gradientColors: ['#ff9a56', '#ff6a88'],
    textColor: '#ffffff',
    style: 'gradient',
    backgroundColor: '#ff9a56',
  },
  {
    id: 'emerald-gradient',
    name: 'Emerald Sea',
    gradientColors: ['#2af598', '#009efd'],
    textColor: '#ffffff',
    style: 'gradient',
    backgroundColor: '#2af598',
  },
  {
    id: 'rose-gold',
    name: 'Rose Gold',
    gradientColors: ['#f7971e', '#ffd200'],
    textColor: '#2C1810',
    style: 'gradient',
    backgroundColor: '#f7971e',
  },
];

const FONTS = [
  // Serif - Elegant & Classic
  { name: 'Playfair Display', value: 'Playfair Display', category: 'Serif' },
  { name: 'Merriweather', value: 'Merriweather', category: 'Serif' },
  { name: 'Lora', value: 'Lora', category: 'Serif' },
  { name: 'Crimson Text', value: 'Crimson Text', category: 'Serif' },
  {
    name: 'Cormorant Garamond',
    value: 'Cormorant Garamond',
    category: 'Serif',
  },
  { name: 'EB Garamond', value: 'EB Garamond', category: 'Serif' },
  { name: 'Libre Baskerville', value: 'Libre Baskerville', category: 'Serif' },
  { name: 'Spectral', value: 'Spectral', category: 'Serif' },
  { name: 'Bitter', value: 'Bitter', category: 'Serif' },
  { name: 'Alegreya', value: 'Alegreya', category: 'Serif' },
  { name: 'Cardo', value: 'Cardo', category: 'Serif' },

  // Sans Serif - Modern & Clean
  { name: 'Poppins', value: 'Poppins', category: 'Sans Serif' },
  { name: 'Montserrat', value: 'Montserrat', category: 'Sans Serif' },
  { name: 'Inter', value: 'Inter', category: 'Sans Serif' },
  { name: 'Roboto', value: 'Roboto', category: 'Sans Serif' },
  { name: 'Open Sans', value: 'Open Sans', category: 'Sans Serif' },
  { name: 'Raleway', value: 'Raleway', category: 'Sans Serif' },
  { name: 'Nunito', value: 'Nunito', category: 'Sans Serif' },
  { name: 'Work Sans', value: 'Work Sans', category: 'Sans Serif' },
  { name: 'DM Sans', value: 'DM Sans', category: 'Sans Serif' },
  { name: 'Space Grotesk', value: 'Space Grotesk', category: 'Sans Serif' },
  { name: 'Barlow', value: 'Barlow', category: 'Sans Serif' },
  { name: 'Manrope', value: 'Manrope', category: 'Sans Serif' },
  { name: 'Red Hat Display', value: 'Red Hat Display', category: 'Sans Serif' },
  { name: 'Outfit', value: 'Outfit', category: 'Sans Serif' },
  { name: 'Karla', value: 'Karla', category: 'Sans Serif' },

  // Display & Heading - Bold & Impactful
  { name: 'Bebas Neue', value: 'Bebas Neue', category: 'Display' },
  { name: 'Oswald', value: 'Oswald', category: 'Display' },
  { name: 'Anton', value: 'Anton', category: 'Display' },
  { name: 'Righteous', value: 'Righteous', category: 'Display' },
  { name: 'Archivo Black', value: 'Archivo Black', category: 'Display' },
  { name: 'Alfa Slab One', value: 'Alfa Slab One', category: 'Display' },
  { name: 'Lexend', value: 'Lexend', category: 'Display' },
  { name: 'Syne', value: 'Syne', category: 'Display' },
  { name: 'Chakra Petch', value: 'Chakra Petch', category: 'Display' },
  { name: 'Rajdhani', value: 'Rajdhani', category: 'Display' },

  // Script & Elegant
  { name: 'Dancing Script', value: 'Dancing Script', category: 'Script' },
  { name: 'Pacifico', value: 'Pacifico', category: 'Script' },
  { name: 'Great Vibes', value: 'Great Vibes', category: 'Script' },
  { name: 'Satisfy', value: 'Satisfy', category: 'Script' },
  { name: 'Allura', value: 'Allura', category: 'Script' },
  { name: 'Cookie', value: 'Cookie', category: 'Script' },

  // Handwriting - Casual & Personal
  { name: 'Caveat', value: 'Caveat', category: 'Handwriting' },
  {
    name: 'Architects Daughter',
    value: 'Architects Daughter',
    category: 'Handwriting',
  },
  { name: 'Indie Flower', value: 'Indie Flower', category: 'Handwriting' },
  {
    name: 'Permanent Marker',
    value: 'Permanent Marker',
    category: 'Handwriting',
  },
  {
    name: 'Shadows Into Light',
    value: 'Shadows Into Light',
    category: 'Handwriting',
  },
  { name: 'Kalam', value: 'Kalam', category: 'Handwriting' },
  { name: 'Patrick Hand', value: 'Patrick Hand', category: 'Handwriting' },
  { name: 'Reenie Beanie', value: 'Reenie Beanie', category: 'Handwriting' },

  // Monospace - Code & Technical
  { name: 'Fira Code', value: 'Fira Code', category: 'Monospace' },
  { name: 'JetBrains Mono', value: 'JetBrains Mono', category: 'Monospace' },
  { name: 'Source Code Pro', value: 'Source Code Pro', category: 'Monospace' },
  { name: 'IBM Plex Mono', value: 'IBM Plex Mono', category: 'Monospace' },
  { name: 'Roboto Mono', value: 'Roboto Mono', category: 'Monospace' },
  { name: 'Space Mono', value: 'Space Mono', category: 'Monospace' },
  { name: 'Courier Prime', value: 'Courier Prime', category: 'Monospace' },

  // Decorative & Unique
  { name: 'Fredoka', value: 'Fredoka', category: 'Decorative' },
  { name: 'Comfortaa', value: 'Comfortaa', category: 'Decorative' },
  { name: 'Abril Fatface', value: 'Abril Fatface', category: 'Decorative' },
  { name: 'Lobster', value: 'Lobster', category: 'Decorative' },
  { name: 'Bangers', value: 'Bangers', category: 'Decorative' },
  { name: 'Audiowide', value: 'Audiowide', category: 'Decorative' },
];

const AVAILABLE_FONTS = FONTS.map(f => f.value);

const STORAGE_KEY = 'status-creator-config';

const StatusImageCreator: React.FC = () => {
  useEffect(() => {
    document.title = 'Status Image Creator | NiveshRight';
  }, []);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to get saved config
  const getSavedConfig = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const savedConfig = getSavedConfig();

  const [contentType, setContentType] = useState<'lesson' | 'quote' | 'custom'>(
    savedConfig.contentType || 'lesson'
  );
  const [mainText, setMainText] = useState(savedConfig.mainText || 'Heading');
  const [subText, setSubText] = useState(savedConfig.subText || 'Text here');
  const [quoteText, setQuoteText] = useState(
    savedConfig.quoteText || 'Quote here'
  );
  const [quoteAuthor, setQuoteAuthor] = useState(
    savedConfig.quoteAuthor || 'Author'
  );

  // Decorative elements (available in all modes)
  const [decorativeElements, setDecorativeElements] = useState<ContentBlock[]>(
    savedConfig.decorativeElements || []
  );

  // Building blocks for custom content
  const defaultContentBlocks: ContentBlock[] = [
    {
      id: '1',
      type: 'heading',
      content: 'Heading',
      fontSize: 70,
      fontWeight: 'normal',
      fontStyle: 'normal',
      alignment: 'left',
    },
    {
      id: '2',
      type: 'spacer',
      content: '',
    },
    {
      id: '3',
      type: 'text',
      content: 'Text here',
      fontSize: 50,
      fontWeight: 'normal',
      fontStyle: 'normal',
      alignment: 'left',
    },
  ];

  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>(
    savedConfig.contentBlocks || defaultContentBlocks
  );

  const [selectedTemplate, setSelectedTemplate] = useState<Template>(
    savedConfig.selectedTemplateId
      ? TEMPLATES.find(t => t.id === savedConfig.selectedTemplateId) ||
          TEMPLATES[0]
      : TEMPLATES[0]
  );
  const [font] = useState('Playfair Display'); // Default fallback for custom blocks
  const [mainTextFont, setMainTextFont] = useState(
    savedConfig.mainTextFont || 'Playfair Display'
  );
  const [subTextFont, setSubTextFont] = useState(
    savedConfig.subTextFont || 'Playfair Display'
  );
  const [quoteTextFont, setQuoteTextFont] = useState(
    savedConfig.quoteTextFont || 'Playfair Display'
  );
  const [quoteAuthorFont, setQuoteAuthorFont] = useState(
    savedConfig.quoteAuthorFont || 'Playfair Display'
  );
  const [quoteTextStyle, setQuoteTextStyle] = useState<
    'normal' | 'italic' | 'bold' | 'bold-italic'
  >(savedConfig.quoteTextStyle || 'italic');
  const [quoteTextSize, setQuoteTextSize] = useState(
    savedConfig.quoteTextSize || 72
  );
  const [showContactInfo, setShowContactInfo] = useState(
    savedConfig.showContactInfo !== undefined
      ? savedConfig.showContactInfo
      : true
  );
  const [logoPosition, setLogoPosition] = useState<'top' | 'bottom'>(
    savedConfig.logoPosition || 'bottom'
  );
  const [logoSize, setLogoSize] = useState(savedConfig.logoSize || 200);
  const [logoVariant, setLogoVariant] = useState<'full' | 'mini' | 'full-plus'>(
    savedConfig.logoVariant || 'full-plus'
  );
  const [logoGrayscale, setLogoGrayscale] = useState(
    savedConfig.logoGrayscale || 0
  );
  const [customBackground, setCustomBackground] = useState<string | null>(
    savedConfig.customBackground || null
  );
  const [bgImageScale, setBgImageScale] = useState(
    savedConfig.bgImageScale || 1
  );
  const [bgImageOpacity, setBgImageOpacity] = useState(
    savedConfig.bgImageOpacity || 1
  );
  const [bgImageX, setBgImageX] = useState(
    savedConfig.bgImageX !== undefined ? savedConfig.bgImageX : 0
  );
  const [bgImageY, setBgImageY] = useState(
    savedConfig.bgImageY !== undefined ? savedConfig.bgImageY : 0
  );
  const [logoImage, setLogoImage] = useState<HTMLImageElement | null>(null);

  // Quote mark settings
  const [showQuoteMark, setShowQuoteMark] = useState(
    savedConfig.showQuoteMark !== undefined ? savedConfig.showQuoteMark : true
  );
  const [quoteMarkStyle, setQuoteMarkStyle] = useState<
    'decorative-1' | 'decorative-2' | 'decorative-3'
  >(savedConfig.quoteMarkStyle || 'decorative-1');
  const [quoteMarkSize, setQuoteMarkSize] = useState(
    savedConfig.quoteMarkSize || 200
  );
  const [quoteMarkX, setQuoteMarkX] = useState(
    savedConfig.quoteMarkX !== undefined ? savedConfig.quoteMarkX : 540
  );
  const [quoteMarkY, setQuoteMarkY] = useState(
    savedConfig.quoteMarkY !== undefined ? savedConfig.quoteMarkY : 350
  );

  // Logo dragging
  const [logoX, setLogoX] = useState<number | null>(
    savedConfig.logoX !== undefined ? savedConfig.logoX : null
  );
  const [logoY, setLogoY] = useState<number | null>(
    savedConfig.logoY !== undefined ? savedConfig.logoY : null
  );
  const [contactX, setContactX] = useState<number | null>(
    savedConfig.contactX !== undefined ? savedConfig.contactX : null
  );
  const [contactY, setContactY] = useState<number | null>(
    savedConfig.contactY !== undefined ? savedConfig.contactY : null
  );
  const [isDraggingQuote, setIsDraggingQuote] = useState(false);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [isDraggingContact, setIsDraggingContact] = useState(false);
  const [draggingBlockId, setDraggingBlockId] = useState<string | null>(null);

  // Contact info settings
  const [contactFontSize, setContactFontSize] = useState(
    savedConfig.contactFontSize || 35
  );
  const [contactOpacity, setContactOpacity] = useState(
    savedConfig.contactOpacity !== undefined ? savedConfig.contactOpacity : 0.8
  );
  const [contactColor, setContactColor] = useState(
    savedConfig.contactColor || ''
  );

  // Logo section settings
  const [logoSectionOpacity, setLogoSectionOpacity] = useState(
    savedConfig.logoSectionOpacity !== undefined
      ? savedConfig.logoSectionOpacity
      : 1
  );

  // Save configuration to localStorage whenever any value changes
  useEffect(() => {
    const config = {
      contentType,
      mainText,
      subText,
      quoteText,
      quoteAuthor,
      decorativeElements,
      contentBlocks,
      selectedTemplateId: selectedTemplate.id,
      mainTextFont,
      subTextFont,
      quoteTextFont,
      quoteAuthorFont,
      quoteTextStyle,
      quoteTextSize,
      showContactInfo,
      logoPosition,
      logoSize,
      logoVariant,
      logoGrayscale,
      customBackground,
      bgImageScale,
      bgImageOpacity,
      bgImageX,
      bgImageY,
      showQuoteMark,
      quoteMarkStyle,
      quoteMarkSize,
      quoteMarkX,
      quoteMarkY,
      logoX,
      logoY,
      contactX,
      contactY,
      contactFontSize,
      contactOpacity,
      contactColor,
      logoSectionOpacity,
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (error) {
      console.error('Failed to save configuration:', error);
    }
  }, [
    contentType,
    mainText,
    subText,
    quoteText,
    quoteAuthor,
    decorativeElements,
    contentBlocks,
    selectedTemplate,
    mainTextFont,
    subTextFont,
    quoteTextFont,
    quoteAuthorFont,
    quoteTextStyle,
    quoteTextSize,
    showContactInfo,
    logoPosition,
    logoSize,
    logoVariant,
    logoGrayscale,
    customBackground,
    bgImageScale,
    bgImageOpacity,
    bgImageX,
    bgImageY,
    showQuoteMark,
    quoteMarkStyle,
    quoteMarkSize,
    quoteMarkX,
    quoteMarkY,
    logoX,
    logoY,
    contactX,
    contactY,
    contactFontSize,
    contactOpacity,
    contactColor,
    logoSectionOpacity,
  ]);

  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?${FONTS.map(
      f =>
        `family=${f.value.replace(' ', '+')}:wght@300;400;500;600;700;800;900&display=swap`
    ).join('&')}`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Load logo based on variant
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const logoFiles = {
      full: '/logo-full.svg',
      mini: '/logo-mini.svg',
      'full-plus': '/logo-full-plus.svg',
    };
    img.src = logoFiles[logoVariant];
    img.onload = () => setLogoImage(img);
  }, [logoVariant]);

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !logoImage) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, 1080, 1920);

    // Draw background
    if (customBackground) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = customBackground;
      img.onload = () => {
        // Fill canvas with image (cover)
        const baseScale = Math.max(1080 / img.width, 1920 / img.height);
        const scale = baseScale * bgImageScale;
        const x = (1080 - img.width * scale) / 2 + bgImageX;
        const y = (1920 - img.height * scale) / 2 + bgImageY;

        // Apply opacity
        ctx.globalAlpha = bgImageOpacity;
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        ctx.globalAlpha = 1;

        drawContent(ctx);
      };
      return;
    } else if (selectedTemplate.gradientColors) {
      const gradient = ctx.createLinearGradient(0, 0, 0, 1920);
      gradient.addColorStop(0, selectedTemplate.gradientColors[0]);
      gradient.addColorStop(1, selectedTemplate.gradientColors[1]);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 1080, 1920);
    } else {
      ctx.fillStyle = selectedTemplate.backgroundColor;
      ctx.fillRect(0, 0, 1080, 1920);
    }

    drawContent(ctx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contentType,
    mainText,
    subText,
    quoteText,
    quoteAuthor,
    selectedTemplate,
    font,
    mainTextFont,
    subTextFont,
    quoteTextFont,
    quoteAuthorFont,
    quoteTextStyle,
    quoteTextSize,
    showContactInfo,
    logoPosition,
    logoSize,
    logoVariant,
    logoGrayscale,
    customBackground,
    bgImageScale,
    bgImageOpacity,
    bgImageX,
    bgImageY,
    logoImage,
    showQuoteMark,
    quoteMarkStyle,
    quoteMarkSize,
    quoteMarkX,
    quoteMarkY,
    logoX,
    logoY,
    contactX,
    contactY,
    contactFontSize,
    contactOpacity,
    contactColor,
    logoSectionOpacity,
    contentBlocks,
    decorativeElements,
  ]);

  const drawStylishQuoteMark = (
    ctx: CanvasRenderingContext2D,
    style: string,
    x: number,
    y: number,
    size: number,
    color: string
  ) => {
    ctx.save();
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.2;

    switch (style) {
      case 'decorative-1': {
        // SVG quote-1.svg - Elegant rounded quotes (24x24 viewBox)
        const scale = size / 24;
        // Center the SVG (24x24 viewBox, so offset by half)
        ctx.translate(x - (24 * scale) / 2, y - (24 * scale) / 2);
        ctx.scale(scale, scale);

        // First quote
        const path1 =
          'M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 0 1-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z';
        const p1 = new Path2D(path1);
        ctx.fill(p1);
        // Second quote
        const path2 =
          'M14.583 17.321C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 0 1-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z';
        const p2 = new Path2D(path2);
        ctx.fill(p2);
        break;
      }

      case 'decorative-2': {
        // SVG quote-2.svg - Block style quotes (32x32 viewBox)
        const scale = size / 32;
        // Center the SVG (32x32 viewBox, so offset by half)
        ctx.translate(x - (32 * scale) / 2, y - (32 * scale) / 2);
        ctx.scale(scale, scale);

        const path2_1 =
          'M12,15H6.11A9,9,0,0,1,10,8.86l1.79-1.2L10.69,6,8.9,7.2A11,11,0,0,0,4,16.35V23a2,2,0,0,0,2,2h6a2,2,0,0,0,2-2V17A2,2,0,0,0,12,15Z';
        const p2_1 = new Path2D(path2_1);
        ctx.fill(p2_1);
        const path2_2 =
          'M26,15H20.11A9,9,0,0,1,24,8.86l1.79-1.2L24.7,6,22.9,7.2A11,11,0,0,0,18,16.35V23a2,2,0,0,0,2,2h6a2,2,0,0,0,2-2V17A2,2,0,0,0,26,15Z';
        const p2_2 = new Path2D(path2_2);
        ctx.fill(p2_2);
        break;
      }

      case 'decorative-3': {
        // SVG quote-3.svg - Circular comma style (32x32 viewBox)
        const scale = size / 32;
        // Center the SVG (32x32 viewBox, so offset by half)
        ctx.translate(x - (32 * scale) / 2, y - (32 * scale) / 2);
        ctx.scale(scale, scale);

        const path3_1 =
          'M13,11c0.6,0,1-0.4,1-1s-0.4-1-1-1c-5,0-9,4-9,9c0,2.8,2.2,5,5,5s5-2.2,5-5s-2.2-5-5-5c-0.3,0-0.7,0-1,0.1C9.3,11.8,11,11,13,11z';
        const p3_1 = new Path2D(path3_1);
        ctx.fill(p3_1);
        const path3_2 =
          'M23,13c-0.3,0-0.7,0-1,0.1c1.3-1.3,3-2.1,5-2.1c0.6,0,1-0.4,1-1s-0.4-1-1-1c-5,0-9,4-9,9c0,2.8,2.2,5,5,5s5-2.2,5-5S25.8,13,23,13z';
        const p3_2 = new Path2D(path3_2);
        ctx.fill(p3_2);
        break;
      }
    }

    ctx.restore();
  };

  const drawContent = (ctx: CanvasRenderingContext2D) => {
    if (!logoImage) return;

    // Calculate positions
    const centerY = 960;
    const logoHeight = logoSize;
    const logoWidth = (logoImage.width / logoImage.height) * logoHeight;
    // Use custom position if set, otherwise use position setting
    const logoYPos =
      logoY !== null
        ? logoY
        : logoPosition === 'top'
          ? 30
          : 1920 - logoHeight - 30;
    // Default logo to bottom-left instead of center
    const logoXPos = logoX !== null ? logoX : 30;

    ctx.fillStyle = selectedTemplate.textColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (contentType === 'quote') {
      // QUOTE STYLE
      // Draw opening quote mark (if enabled)
      if (showQuoteMark) {
        drawStylishQuoteMark(
          ctx,
          quoteMarkStyle,
          quoteMarkX,
          quoteMarkY,
          quoteMarkSize,
          selectedTemplate.textColor
        );
      }

      // Draw quote text
      const quoteFontSize = quoteTextSize;
      const fontWeight =
        quoteTextStyle === 'bold' || quoteTextStyle === 'bold-italic'
          ? 'bold'
          : 'normal';
      const fontStyle =
        quoteTextStyle === 'italic' || quoteTextStyle === 'bold-italic'
          ? 'italic '
          : '';
      ctx.font = `${fontStyle}${fontWeight} ${quoteFontSize}px "${quoteTextFont}"`;
      const quoteLines = wrapText(ctx, quoteText, 880);
      const quoteHeight = quoteLines.length * quoteFontSize * 1.3;
      const quoteStartY = centerY - quoteHeight / 2;

      quoteLines.forEach((line, i) => {
        ctx.fillText(line, 540, quoteStartY + i * quoteFontSize * 1.3);
      });

      // Draw author with dash
      const authorY = quoteStartY + quoteHeight + 80;
      ctx.font = `500 42px "${quoteAuthorFont}"`;
      ctx.fillText(`— ${quoteAuthor}`, 540, authorY);
    } else if (contentType === 'custom') {
      // CUSTOM BUILDING BLOCKS
      let currentY = logoPosition === 'top' ? logoYPos + logoHeight + 100 : 200;
      const padding = 100; // Side padding

      contentBlocks.forEach(block => {
        ctx.fillStyle = selectedTemplate.textColor;

        if (block.type === 'heading' || block.type === 'text') {
          const fontSize = block.fontSize || 50;
          const weight =
            block.fontWeight === 'bold'
              ? 'bold'
              : block.fontWeight === 'light'
                ? '300'
                : '400';
          const style = block.fontStyle === 'italic' ? 'italic ' : '';
          const blockFont = block.fontFamily || font;
          ctx.font = `${style}${weight} ${fontSize}px "${blockFont}"`;

          const blockY = block.y !== undefined ? block.y : currentY;
          const lines = wrapText(ctx, block.content, 1080 - padding * 2);
          const lineHeight = fontSize * 1.3;

          // Set alignment
          if (block.alignment === 'left') {
            ctx.textAlign = 'left';
            lines.forEach((line, i) => {
              ctx.fillText(line, padding, blockY + i * lineHeight);
            });
          } else if (block.alignment === 'right') {
            ctx.textAlign = 'right';
            lines.forEach((line, i) => {
              ctx.fillText(line, 1080 - padding, blockY + i * lineHeight);
            });
          } else {
            // center
            ctx.textAlign = 'center';
            lines.forEach((line, i) => {
              ctx.fillText(line, 540, blockY + i * lineHeight);
            });
          }

          if (block.y === undefined) currentY += lines.length * lineHeight;
        } else if (block.type === 'separator-line') {
          ctx.fillStyle = selectedTemplate.textColor;
          const lineY = block.y !== undefined ? block.y : currentY + 20;
          ctx.fillRect(padding, lineY, 1080 - padding * 2, 3);
          if (block.y === undefined) currentY += 60;
        } else if (block.type === 'separator-dots') {
          ctx.fillStyle = selectedTemplate.textColor;
          const dotRadius = 4;
          const dotSpacing = 20;
          const centerX = block.x !== undefined ? block.x : 540;
          const centerY = block.y !== undefined ? block.y : currentY + 30;
          for (let i = -1; i <= 1; i++) {
            ctx.beginPath();
            ctx.arc(
              centerX + i * dotSpacing,
              centerY,
              dotRadius,
              0,
              Math.PI * 2
            );
            ctx.fill();
          }
          if (block.y === undefined) currentY += 80;
        } else if (block.type === 'next-arrow') {
          // Draw next arrow icon
          const arrowX = block.x !== undefined ? block.x : 1080 - padding - 40;
          const arrowY = block.y !== undefined ? block.y : currentY + 30;
          drawArrow(ctx, arrowX, arrowY, block.arrowStyle || 'simple');
          if (block.y === undefined) currentY += 100;
        } else if (block.type === 'spacer') {
          currentY += 60;
        }
      });
    } else {
      // LESSON STYLE
      // Main text - bold and large
      const mainFontSize = 90;
      ctx.font = `bold ${mainFontSize}px "${mainTextFont}"`;

      // Wrap main text
      const mainLines = wrapText(ctx, mainText, 940);
      const mainTextHeight = mainLines.length * mainFontSize * 1.2;

      // Calculate text block position (centered, avoiding logo)
      let textStartY = centerY - mainTextHeight / 2 - 60;

      // Draw main text
      mainLines.forEach((line, i) => {
        ctx.fillText(line, 540, textStartY + i * mainFontSize * 1.2);
      });

      // Draw decorative line (if highlight style)
      if (
        selectedTemplate.style === 'highlight' &&
        selectedTemplate.accentColor
      ) {
        const lineY = textStartY + mainTextHeight + 40;
        ctx.fillStyle = selectedTemplate.accentColor;
        ctx.fillRect(270, lineY, 540, 8);
      } else {
        // Simple line for other styles
        const lineY = textStartY + mainTextHeight + 40;
        ctx.fillStyle = selectedTemplate.textColor;
        ctx.fillRect(420, lineY, 240, 3);
      }

      // Draw subtext
      const subFontSize = 48;
      ctx.font = `${selectedTemplate.style === 'highlight' ? '500' : '400'} ${subFontSize}px "${subTextFont}"`;
      ctx.fillStyle = selectedTemplate.textColor;

      const subLines = subText.split('\n');
      const subTextStartY = textStartY + mainTextHeight + 120;

      subLines.forEach((line: string, i: number) => {
        // Highlight keywords in italics
        if (selectedTemplate.style === 'highlight') {
          const words = line.split(' ');
          let currentX = 540;
          const lineWidth = ctx.measureText(line).width;
          currentX -= lineWidth / 2;

          words.forEach((word: string, wordIndex: number) => {
            const isItalic = /[a-z]+/.test(word) && word.length > 4; // Simple heuristic
            if (isItalic) {
              ctx.font = `italic 500 ${subFontSize}px "${subTextFont}"`;
            } else {
              ctx.font = `500 ${subFontSize}px "${subTextFont}"`;
            }

            ctx.fillText(word, currentX, subTextStartY + i * subFontSize * 1.4);
            currentX += ctx.measureText(word + ' ').width;
          });
        } else {
          ctx.fillText(line, 540, subTextStartY + i * subFontSize * 1.4);
        }
      });
    }

    // Apply overall logo section opacity and grayscale filter
    ctx.save();
    ctx.globalAlpha = logoSectionOpacity;

    // Apply filters to logo
    if (logoGrayscale === 100) {
      // Full white logo: grayscale + invert + brightness
      ctx.filter = 'brightness(0) invert(1)';
    } else if (logoGrayscale > 0) {
      // Partial grayscale
      ctx.filter = `grayscale(${logoGrayscale}%)`;
    }

    // Draw logo
    ctx.drawImage(logoImage, logoXPos, logoYPos, logoWidth, logoHeight);

    // Reset filter for contact info
    ctx.filter = 'none';

    // Draw contact info below/above logo (or at custom position)
    if (showContactInfo) {
      const contactInfo = [
        CONTACT_INFO.phone.display,
        'vishal@niveshright.com',
        'NiveshRight.com',
      ];
      const lineSpacing = contactFontSize * 1.3;

      // Use custom position if set, otherwise position at bottom-right
      // Position contact info to align at canvas bottom
      const defaultContactY =
        logoPosition === 'bottom' ? 1920 - 80 : logoYPos + 120;
      const contactYPos = contactY !== null ? contactY : defaultContactY;
      // Default contact to bottom-right
      const contactXPos = contactX !== null ? contactX : 1080 - 30;

      ctx.font = `400 ${contactFontSize}px "Inter"`;
      ctx.fillStyle = contactColor || selectedTemplate.textColor;
      ctx.textAlign = 'right'; // Right-align contact info
      // Multiply contact opacity with logo section opacity
      ctx.globalAlpha = logoSectionOpacity * contactOpacity;

      contactInfo.forEach((info, i) => {
        // Stack upward from bottom position (reverse order)
        const y = contactYPos - (contactInfo.length - 1 - i) * lineSpacing;
        ctx.fillText(info, contactXPos, y);
      });

      ctx.restore();
      ctx.globalAlpha = 1;
      ctx.textAlign = 'center'; // Reset text alignment
    }

    // Draw decorative elements (available in all modes)
    drawDecorativeElements(ctx);
  };

  const drawDecorativeElements = (ctx: CanvasRenderingContext2D) => {
    decorativeElements.forEach(el => {
      if (!el.x || !el.y) return;

      ctx.fillStyle = selectedTemplate.textColor;
      ctx.strokeStyle = selectedTemplate.textColor;

      if (el.type === 'separator-line') {
        ctx.fillRect(el.x - 200, el.y, 400, 3);
      } else if (el.type === 'separator-dots') {
        const dotRadius = 4;
        const dotSpacing = 20;
        for (let i = -1; i <= 1; i++) {
          ctx.beginPath();
          ctx.arc(el.x + i * dotSpacing, el.y, dotRadius, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (el.type === 'next-arrow') {
        drawArrow(ctx, el.x, el.y, el.arrowStyle || 'simple');
      }
    });
  };

  const drawArrow = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    style: ArrowStyle
  ) => {
    ctx.save();
    ctx.fillStyle = selectedTemplate.textColor;
    ctx.strokeStyle = selectedTemplate.textColor;
    const size = 40;

    switch (style) {
      case 'simple': // Simple chevron >
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(x, y - size / 2);
        ctx.lineTo(x + size, y);
        ctx.lineTo(x, y + size / 2);
        ctx.stroke();
        break;

      case 'circle': // Arrow in circle (outline)
        ctx.lineWidth = 3;
        // Draw circle
        ctx.beginPath();
        ctx.arc(x, y, size / 2, 0, Math.PI * 2);
        ctx.stroke();
        // Draw arrow inside
        ctx.beginPath();
        ctx.moveTo(x - size / 6, y - size / 6);
        ctx.lineTo(x + size / 6, y);
        ctx.lineTo(x - size / 6, y + size / 6);
        ctx.stroke();
        break;

      case 'filled-circle': // Arrow in filled circle
        // Draw filled circle
        ctx.beginPath();
        ctx.arc(x, y, size / 2, 0, Math.PI * 2);
        ctx.fill();
        // Draw white arrow
        ctx.save();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x - size / 6, y - size / 6);
        ctx.lineTo(x + size / 6, y);
        ctx.lineTo(x - size / 6, y + size / 6);
        ctx.stroke();
        ctx.restore();
        break;

      case 'squared': // Simple arrow with line
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        // Draw line
        ctx.beginPath();
        ctx.moveTo(x - size / 2, y);
        ctx.lineTo(x + size / 2, y);
        ctx.stroke();
        // Draw arrowhead
        ctx.beginPath();
        ctx.moveTo(x + size / 4, y - size / 4);
        ctx.lineTo(x + size / 2, y);
        ctx.lineTo(x + size / 4, y + size / 4);
        ctx.stroke();
        break;

      case 'double-angle': // Double chevron >>
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        // First chevron
        ctx.beginPath();
        ctx.moveTo(x - size / 4, y - size / 2);
        ctx.lineTo(x + size / 4, y);
        ctx.lineTo(x - size / 4, y + size / 2);
        ctx.stroke();
        // Second chevron
        ctx.beginPath();
        ctx.moveTo(x + size / 6, y - size / 2);
        ctx.lineTo(x + size * 0.65, y);
        ctx.lineTo(x + size / 6, y + size / 2);
        ctx.stroke();
        break;
    }

    ctx.restore();
  };

  const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
  ) => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);

      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = event => {
      setCustomBackground(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const downloadImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob(blob => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `nivesh-right-status-${Date.now()}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  const resetToTemplate = () => {
    setCustomBackground(null);
  };

  // Block management functions
  const addBlock = (type: BlockType) => {
    const newBlock: ContentBlock = {
      id: Date.now().toString(),
      type,
      content:
        type === 'heading' ? 'Heading' : type === 'text' ? 'Text here' : '',
      fontSize: type === 'heading' ? 70 : 50,
      fontWeight: 'normal',
      fontStyle: 'normal',
      alignment: 'left',
    };
    setContentBlocks([...contentBlocks, newBlock]);
  };

  const removeBlock = (id: string) => {
    setContentBlocks(contentBlocks.filter(block => block.id !== id));
  };

  const updateBlock = (id: string, updates: Partial<ContentBlock>) => {
    setContentBlocks(
      contentBlocks.map(block =>
        block.id === id ? { ...block, ...updates } : block
      )
    );
  };

  // Decorative elements management (for all modes)
  const addDecorative = (type: BlockType) => {
    const newElement: ContentBlock = {
      id: Date.now().toString(),
      type,
      content: '',
      x: 540, // Center
      y: 960, // Middle
    };
    setDecorativeElements([...decorativeElements, newElement]);
  };

  const removeDecorative = (id: string) => {
    setDecorativeElements(decorativeElements.filter(el => el.id !== id));
  };

  const updateDecorative = (id: string, updates: Partial<ContentBlock>) => {
    setDecorativeElements(
      decorativeElements.map(el => (el.id === id ? { ...el, ...updates } : el))
    );
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !logoImage) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = 1080 / rect.width;
    const scaleY = 1920 / rect.height;
    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;

    // Check if clicking on quote mark
    if (contentType === 'quote' && showQuoteMark) {
      const qSize = quoteMarkSize;
      if (
        mouseX >= quoteMarkX - qSize / 2 &&
        mouseX <= quoteMarkX + qSize &&
        mouseY >= quoteMarkY - qSize / 2 &&
        mouseY <= quoteMarkY + qSize
      ) {
        setIsDraggingQuote(true);
        return;
      }
    }

    // Check if clicking on logo
    const logoHeight = logoSize;
    const logoWidth = (logoImage.width / logoImage.height) * logoHeight;
    const currentLogoY =
      logoY !== null
        ? logoY
        : logoPosition === 'top'
          ? 120
          : 1920 - logoHeight - 80;
    const currentLogoX = logoX !== null ? logoX : 80; // Match default position

    // Check if clicking on contact info
    const lineSpacing = contactFontSize * 1.3;
    const defaultContactY =
      logoPosition === 'bottom' ? 1920 - 80 : currentLogoY + logoHeight + 50;
    const currentContactY = contactY !== null ? contactY : defaultContactY;
    const currentContactX = contactX !== null ? contactX : 1080 - 80; // Match default position
    const contactHeight = lineSpacing * 3; // 3 lines of contact info
    const contactWidth = 400; // Approximate width

    // Check if clicking on decorative elements (all modes)
    for (const el of decorativeElements) {
      if (el.x !== undefined && el.y !== undefined) {
        const hitSize = 60; // Hit area size
        if (
          mouseX >= el.x - hitSize &&
          mouseX <= el.x + hitSize &&
          mouseY >= el.y - hitSize &&
          mouseY <= el.y + hitSize
        ) {
          setDraggingBlockId(el.id);
          return;
        }
      }
    }

    // Check if clicking on custom blocks (separators/arrows)
    if (contentType === 'custom') {
      // Calculate positions similar to drawing logic
      let currentY = 150;
      const padding = 100;

      // Create temporary canvas context for text measurements
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      for (const block of contentBlocks) {
        if (block.type === 'heading' || block.type === 'text') {
          // Check if clicking on text/heading block
          const fontSize = block.fontSize || 50;
          const blockFont = block.fontFamily || font;
          ctx.font = `${block.fontWeight === 'bold' ? 'bold' : block.fontWeight === 'light' ? '300' : '400'} ${block.fontStyle === 'italic' ? 'italic' : ''} ${fontSize}px "${blockFont}"`;
          const lines = wrapText(ctx, block.content, 1080 - padding * 2);
          const lineHeight =
            block.type === 'heading' ? fontSize * 1.2 : fontSize * 1.4;
          const blockHeight = lines.length * lineHeight;

          const blockY = block.y !== undefined ? block.y : currentY;

          // Create hit area for text blocks
          const hitMargin = 20;
          if (
            mouseX >= padding - hitMargin &&
            mouseX <= 1080 - padding + hitMargin &&
            mouseY >= blockY - hitMargin &&
            mouseY <= blockY + blockHeight + hitMargin
          ) {
            setDraggingBlockId(block.id);
            return;
          }

          if (block.y === undefined) currentY += blockHeight + 20;
        } else if (block.type === 'separator-line') {
          const lineY = block.y !== undefined ? block.y : currentY + 20;
          const hitMargin = 30;

          // Line spans full width, check if mouse is within vertical range
          if (
            mouseX >= padding &&
            mouseX <= 1080 - padding &&
            mouseY >= lineY - hitMargin &&
            mouseY <= lineY + hitMargin
          ) {
            setDraggingBlockId(block.id);
            return;
          }
          if (block.y === undefined) currentY += 60;
        } else if (block.type === 'separator-dots') {
          const centerX = block.x !== undefined ? block.x : 540;
          const centerY = block.y !== undefined ? block.y : currentY + 30;
          const hitSize = 60;

          if (
            mouseX >= centerX - hitSize &&
            mouseX <= centerX + hitSize &&
            mouseY >= centerY - hitSize &&
            mouseY <= centerY + hitSize
          ) {
            setDraggingBlockId(block.id);
            return;
          }
          if (block.y === undefined) currentY += 80;
        } else if (block.type === 'next-arrow') {
          const arrowX = block.x !== undefined ? block.x : 1080 - padding - 40;
          const arrowY = block.y !== undefined ? block.y : currentY + 30;
          const hitSize = 60;

          if (
            mouseX >= arrowX - hitSize &&
            mouseX <= arrowX + hitSize &&
            mouseY >= arrowY - hitSize &&
            mouseY <= arrowY + hitSize
          ) {
            setDraggingBlockId(block.id);
            return;
          }
          if (block.y === undefined) currentY += 100;
        } else if (block.type === 'spacer') {
          currentY += 60;
        }
      }
    }

    // Check contact info first (higher priority) - check from right side
    if (
      mouseX >= currentContactX - contactWidth &&
      mouseX <= currentContactX &&
      mouseY >= currentContactY - contactHeight &&
      mouseY <= currentContactY + 30
    ) {
      setIsDraggingContact(true);
      return;
    }

    // Then check logo
    if (
      mouseX >= currentLogoX &&
      mouseX <= currentLogoX + logoWidth &&
      mouseY >= currentLogoY &&
      mouseY <= currentLogoY + logoHeight
    ) {
      setIsDraggingLogo(true);
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (
      !isDraggingQuote &&
      !isDraggingLogo &&
      !isDraggingContact &&
      !draggingBlockId
    )
      return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = 1080 / rect.width;
    const scaleY = 1920 / rect.height;
    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;

    if (isDraggingQuote) {
      setQuoteMarkX(Math.max(0, Math.min(1080, mouseX)));
      setQuoteMarkY(Math.max(0, Math.min(1920, mouseY)));
    }

    if (isDraggingLogo) {
      setLogoX(Math.max(0, Math.min(1080, mouseX)));
      setLogoY(Math.max(0, Math.min(1920, mouseY)));
    }

    if (isDraggingContact) {
      setContactX(Math.max(0, Math.min(1080, mouseX)));
      setContactY(Math.max(0, Math.min(1920, mouseY)));
    }

    if (draggingBlockId) {
      const updates = {
        x: Math.max(0, Math.min(1080, mouseX)),
        y: Math.max(0, Math.min(1920, mouseY)),
      };
      // Try updating in both lists
      if (contentBlocks.some(b => b.id === draggingBlockId)) {
        updateBlock(draggingBlockId, updates);
      } else if (decorativeElements.some(el => el.id === draggingBlockId)) {
        updateDecorative(draggingBlockId, updates);
      }
    }
  };

  const handleCanvasMouseUp = () => {
    setIsDraggingQuote(false);
    setIsDraggingLogo(false);
    setIsDraggingContact(false);
    setDraggingBlockId(null);
  };

  const resetQuotePosition = () => {
    setQuoteMarkX(540); // Center of 1080px width
    setQuoteMarkY(350);
  };

  const resetLogoPosition = () => {
    setLogoX(null);
    setLogoY(null);
  };

  const resetContactPosition = () => {
    setContactX(null);
    setContactY(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <main className="w-full px-2 sm:px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Canvas Preview */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-bold mb-3 lg:mb-4 text-gray-900">
                  Preview
                </h3>
                <div className="flex justify-center bg-gray-100 rounded-lg p-2 lg:p-4">
                  <canvas
                    ref={canvasRef}
                    width={1080}
                    height={1920}
                    className="border-2 border-gray-300 rounded-lg shadow-xl cursor-move"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      maxHeight: '70vh',
                    }}
                    onMouseDown={handleCanvasMouseDown}
                    onMouseMove={handleCanvasMouseMove}
                    onMouseUp={handleCanvasMouseUp}
                    onMouseLeave={handleCanvasMouseUp}
                  />
                </div>
                <div className="mt-3 lg:mt-4 flex gap-2 lg:gap-3 flex-wrap">
                  <Button onClick={downloadImage} icon={Download}>
                    <span className="hidden sm:inline">Download PNG</span>
                    <span className="sm:hidden">Download</span>
                  </Button>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="secondary"
                    icon={Upload}
                  >
                    <span className="hidden sm:inline">Upload Background</span>
                    <span className="sm:hidden">Upload</span>
                  </Button>
                  {customBackground && (
                    <Button
                      onClick={resetToTemplate}
                      variant="secondary"
                      icon={RefreshCw}
                    >
                      <span className="hidden sm:inline">Reset Background</span>
                      <span className="sm:hidden">Reset</span>
                    </Button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>

                {/* Background Image Controls */}
                {customBackground && (
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                        Image Scale: {(bgImageScale * 100).toFixed(0)}%
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={bgImageScale}
                        onChange={e =>
                          setBgImageScale(parseFloat(e.target.value))
                        }
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                        Image Opacity: {(bgImageOpacity * 100).toFixed(0)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={bgImageOpacity}
                        onChange={e =>
                          setBgImageOpacity(parseFloat(e.target.value))
                        }
                        className="w-full"
                      />
                    </div>

                    <div className="p-3 bg-gray-50 rounded-lg">
                      <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                        Position Background
                      </label>
                      <div className="flex flex-col items-center gap-2">
                        <button
                          onClick={() =>
                            setBgImageY((prev: number) => prev - 20)
                          }
                          className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50"
                          title="Move Up"
                        >
                          ▲
                        </button>
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              setBgImageX((prev: number) => prev - 20)
                            }
                            className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50"
                            title="Move Left"
                          >
                            ◄
                          </button>
                          <button
                            onClick={() => {
                              setBgImageX(0);
                              setBgImageY(0);
                            }}
                            className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-xs"
                            title="Reset Position"
                          >
                            Center
                          </button>
                          <button
                            onClick={() =>
                              setBgImageX((prev: number) => prev + 20)
                            }
                            className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50"
                            title="Move Right"
                          >
                            ►
                          </button>
                        </div>
                        <button
                          onClick={() =>
                            setBgImageY((prev: number) => prev + 20)
                          }
                          className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50"
                          title="Move Down"
                        >
                          ▼
                        </button>
                      </div>
                      {(bgImageX !== 0 || bgImageY !== 0) && (
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          Offset: X={bgImageX}px, Y={bgImageY}px
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Decorative Elements */}
                <div className="mt-6 p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Decorative Elements
                  </h4>
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => addDecorative('separator-line')}
                      className="px-3 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      + Line
                    </button>
                    <button
                      onClick={() => addDecorative('separator-dots')}
                      className="px-3 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      + Dots
                    </button>
                    <button
                      onClick={() => addDecorative('next-arrow')}
                      className="px-3 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      + Arrow
                    </button>
                  </div>
                  {decorativeElements.length > 0 && (
                    <div className="space-y-2">
                      {decorativeElements.map(el => (
                        <div
                          key={el.id}
                          className="p-3 bg-white rounded border"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">
                              {el.type.replace('-', ' ')}
                            </span>
                            <button
                              onClick={() => removeDecorative(el.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>

                          {el.type === 'next-arrow' && (
                            <div className="mt-2">
                              <label className="block text-xs text-gray-600 mb-1">
                                Arrow Style
                              </label>
                              <select
                                value={el.arrowStyle || 'simple'}
                                onChange={e =>
                                  updateDecorative(el.id, {
                                    arrowStyle: e.target.value as any,
                                  })
                                }
                                className="w-full px-2 py-1 border rounded text-xs"
                              >
                                <option value="simple">Simple Chevron</option>
                                <option value="circle">Circle Outline</option>
                                <option value="filled-circle">
                                  Filled Circle
                                </option>
                                <option value="squared">Arrow with Line</option>
                                <option value="double-angle">
                                  Double Chevron
                                </option>
                              </select>
                            </div>
                          )}
                        </div>
                      ))}
                      <p className="text-xs text-gray-500 mt-2">
                        Drag elements on canvas to position them
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Controls Panel */}
          <div className="space-y-4 lg:space-y-6">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-bold mb-3 lg:mb-4 text-gray-900">
                  Content
                </h3>
                <div className="space-y-2.5 lg:space-y-4">
                  {/* Content Type Selector */}
                  <div>
                    <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-2">
                      Content Type
                    </label>
                    <div className="grid grid-cols-3 gap-1.5 lg:gap-2">
                      <button
                        onClick={() => setContentType('lesson')}
                        className={`px-2 lg:px-4 py-1.5 lg:py-2 text-sm lg:text-base rounded-lg border transition-all ${
                          contentType === 'lesson'
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        Lesson
                      </button>
                      <button
                        onClick={() => setContentType('quote')}
                        className={`px-2 lg:px-4 py-1.5 lg:py-2 text-sm lg:text-base rounded-lg border transition-all ${
                          contentType === 'quote'
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        Quote
                      </button>
                      <button
                        onClick={() => setContentType('custom')}
                        className={`px-2 lg:px-4 py-1.5 lg:py-2 text-sm lg:text-base rounded-lg border transition-all ${
                          contentType === 'custom'
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        Custom
                      </button>
                    </div>
                  </div>

                  {contentType === 'lesson' && (
                    <>
                      <div>
                        <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                          Main Text
                        </label>
                        <textarea
                          value={mainText}
                          onChange={e => setMainText(e.target.value)}
                          className="w-full px-2.5 lg:px-3 py-1.5 lg:py-2 text-sm lg:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-1.5 lg:mb-2"
                          rows={2}
                          placeholder="Headline"
                        />
                        <select
                          value={mainTextFont}
                          onChange={e => setMainTextFont(e.target.value)}
                          className="w-full px-2.5 lg:px-3 py-1.5 lg:py-2 text-sm lg:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {AVAILABLE_FONTS.map(f => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                          Subtext
                        </label>
                        <textarea
                          value={subText}
                          onChange={e => setSubText(e.target.value)}
                          className="w-full px-2.5 lg:px-3 py-1.5 lg:py-2 text-sm lg:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-1.5 lg:mb-2"
                          rows={3}
                          placeholder="Description"
                        />
                        <select
                          value={subTextFont}
                          onChange={e => setSubTextFont(e.target.value)}
                          className="w-full px-2.5 lg:px-3 py-1.5 lg:py-2 text-sm lg:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {AVAILABLE_FONTS.map(f => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {contentType === 'quote' && (
                    <>
                      <div>
                        <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                          Quote Text
                        </label>
                        <textarea
                          value={quoteText}
                          onChange={e => setQuoteText(e.target.value)}
                          className="w-full px-2.5 lg:px-3 py-1.5 lg:py-2 text-sm lg:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-1.5 lg:mb-2"
                          rows={4}
                          placeholder="Quote here"
                        />

                        <div className="mb-2">
                          <label className="block text-xs text-gray-600 mb-1">
                            Text Style
                          </label>
                          <div className="grid grid-cols-4 gap-1.5 lg:gap-2">
                            <button
                              onClick={() => setQuoteTextStyle('normal')}
                              className={`px-2 lg:px-3 py-1.5 lg:py-2 text-xs lg:text-sm rounded-lg border-2 transition-all ${
                                quoteTextStyle === 'normal'
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              Normal
                            </button>
                            <button
                              onClick={() => setQuoteTextStyle('italic')}
                              className={`px-2 lg:px-3 py-1.5 lg:py-2 text-xs lg:text-sm italic rounded-lg border-2 transition-all ${
                                quoteTextStyle === 'italic'
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              Italic
                            </button>
                            <button
                              onClick={() => setQuoteTextStyle('bold')}
                              className={`px-2 lg:px-3 py-1.5 lg:py-2 text-xs lg:text-sm font-bold rounded-lg border-2 transition-all ${
                                quoteTextStyle === 'bold'
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              Bold
                            </button>
                            <button
                              onClick={() => setQuoteTextStyle('bold-italic')}
                              className={`px-2 lg:px-3 py-1.5 lg:py-2 text-xs lg:text-sm font-bold italic rounded-lg border-2 transition-all ${
                                quoteTextStyle === 'bold-italic'
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              Bold Italic
                            </button>
                          </div>
                        </div>

                        <div className="mb-2">
                          <label className="block text-xs text-gray-600 mb-1">
                            Font Size: {quoteTextSize}px
                          </label>
                          <input
                            type="range"
                            min="24"
                            max="150"
                            value={quoteTextSize}
                            onChange={e =>
                              setQuoteTextSize(parseInt(e.target.value))
                            }
                            className="w-full"
                          />
                        </div>

                        <select
                          value={quoteTextFont}
                          onChange={e => setQuoteTextFont(e.target.value)}
                          className="w-full px-2.5 lg:px-3 py-1.5 lg:py-2 text-sm lg:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {AVAILABLE_FONTS.map(f => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                          Author Name
                        </label>
                        <input
                          type="text"
                          value={quoteAuthor}
                          onChange={e => setQuoteAuthor(e.target.value)}
                          className="w-full px-2.5 lg:px-3 py-1.5 lg:py-2 text-sm lg:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-1.5 lg:mb-2"
                          placeholder="Author"
                        />
                        <select
                          value={quoteAuthorFont}
                          onChange={e => setQuoteAuthorFont(e.target.value)}
                          className="w-full px-2.5 lg:px-3 py-1.5 lg:py-2 text-sm lg:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {AVAILABLE_FONTS.map(f => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {/* Custom Building Blocks */}
                  {contentType === 'custom' && (
                    <div className="space-y-3 lg:space-y-4">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                        <h4 className="text-sm lg:text-base font-semibold text-gray-900">
                          Content Blocks
                        </h4>
                        <div className="flex gap-1 flex-wrap">
                          <button
                            onClick={() => addBlock('heading')}
                            className="px-2.5 py-1.5 text-xs lg:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                          >
                            + Heading
                          </button>
                          <button
                            onClick={() => addBlock('text')}
                            className="px-2.5 py-1.5 text-xs lg:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                          >
                            + Text
                          </button>
                          <button
                            onClick={() => addBlock('spacer')}
                            className="px-2.5 py-1.5 text-xs lg:text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            + Space
                          </button>
                          <button
                            onClick={() => addBlock('separator-line')}
                            className="px-2.5 py-1.5 text-xs lg:text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            + Line
                          </button>
                          <button
                            onClick={() => addBlock('separator-dots')}
                            className="px-2.5 py-1.5 text-xs lg:text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            + Dots
                          </button>
                          <button
                            onClick={() => addBlock('next-arrow')}
                            className="px-2.5 py-1.5 text-xs lg:text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            + Arrow
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2.5 lg:space-y-3 max-h-80 lg:max-h-96 overflow-y-auto pr-1">
                        {contentBlocks.map((block, index) => (
                          <div
                            key={block.id}
                            className="p-2.5 lg:p-3 border border-gray-200 rounded-lg bg-gray-50 hover:border-gray-300 transition-colors"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <span className="text-xs lg:text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                {block.type.replace('-', ' ')}
                              </span>
                              <button
                                onClick={() => removeBlock(block.id)}
                                className="p-1 hover:bg-red-100 text-red-600 rounded transition-colors"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>

                            <div className="mb-2 p-2 bg-blue-50 border border-blue-100 rounded-lg text-xs">
                              <p className="text-gray-700">
                                <strong className="text-blue-700">
                                  Drag on canvas
                                </strong>{' '}
                                to reposition
                              </p>
                              {(block.x !== undefined ||
                                block.y !== undefined) && (
                                <button
                                  onClick={() =>
                                    updateBlock(block.id, {
                                      x: undefined,
                                      y: undefined,
                                    })
                                  }
                                  className="mt-1.5 px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                                >
                                  Reset Position
                                </button>
                              )}
                            </div>

                            {(block.type === 'heading' ||
                              block.type === 'text') && (
                              <>
                                <textarea
                                  value={block.content}
                                  onChange={e =>
                                    updateBlock(block.id, {
                                      content: e.target.value,
                                    })
                                  }
                                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2"
                                  rows={2}
                                  placeholder={
                                    block.type === 'heading'
                                      ? 'Heading'
                                      : 'Text here'
                                  }
                                />
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <label className="block text-gray-600 mb-1 font-medium">
                                      Size
                                    </label>
                                    <input
                                      type="number"
                                      value={block.fontSize}
                                      onChange={e =>
                                        updateBlock(block.id, {
                                          fontSize: parseInt(e.target.value),
                                        })
                                      }
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      min="12"
                                      max="200"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-gray-600 mb-1 font-medium">
                                      Weight
                                    </label>
                                    <select
                                      value={block.fontWeight}
                                      onChange={e =>
                                        updateBlock(block.id, {
                                          fontWeight: e.target.value as any,
                                        })
                                      }
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="light">Light</option>
                                      <option value="normal">Normal</option>
                                      <option value="bold">Bold</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-gray-600 mb-1 font-medium">
                                      Style
                                    </label>
                                    <select
                                      value={block.fontStyle}
                                      onChange={e =>
                                        updateBlock(block.id, {
                                          fontStyle: e.target.value as any,
                                        })
                                      }
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="normal">Normal</option>
                                      <option value="italic">Italic</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-gray-600 mb-1 font-medium">
                                      Align
                                    </label>
                                    <select
                                      value={block.alignment}
                                      onChange={e =>
                                        updateBlock(block.id, {
                                          alignment: e.target.value as any,
                                        })
                                      }
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="left">Left</option>
                                      <option value="center">Center</option>
                                      <option value="right">Right</option>
                                    </select>
                                  </div>
                                </div>
                                <div className="col-span-2 mt-2">
                                  <label className="block text-gray-600 mb-1 font-medium">
                                    Font
                                  </label>
                                  <select
                                    value={block.fontFamily || font}
                                    onChange={e =>
                                      updateBlock(block.id, {
                                        fontFamily: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    {FONTS.map(f => (
                                      <option key={f.value} value={f.value}>
                                        {f.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </>
                            )}

                            {block.type === 'next-arrow' && (
                              <div className="mt-2">
                                <label className="block text-gray-600 mb-1 text-xs font-medium">
                                  Arrow Style
                                </label>
                                <select
                                  value={block.arrowStyle || 'simple'}
                                  onChange={e =>
                                    updateBlock(block.id, {
                                      arrowStyle: e.target.value as ArrowStyle,
                                    })
                                  }
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                >
                                  <option value="simple">Simple Chevron</option>
                                  <option value="circle">Circle Outline</option>
                                  <option value="filled-circle">
                                    Filled Circle
                                  </option>
                                  <option value="squared">
                                    Arrow with Line
                                  </option>
                                  <option value="double-angle">
                                    Double Chevron
                                  </option>
                                </select>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Template Selection */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-bold mb-3 lg:mb-4 text-gray-900">
                  Templates ({TEMPLATES.length})
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-3 max-h-[400px] lg:max-h-[600px] overflow-y-auto pr-2">
                  {TEMPLATES.map(template => (
                    <button
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setCustomBackground(null);
                      }}
                      className={`p-3 lg:p-4 rounded-lg border-2 transition-all ${
                        selectedTemplate.id === template.id
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                      style={{
                        background: template.gradientColors
                          ? `linear-gradient(135deg, ${template.gradientColors[0]}, ${template.gradientColors[1]})`
                          : template.backgroundColor,
                      }}
                    >
                      <span
                        className="text-xs font-medium block"
                        style={{
                          color: template.textColor,
                        }}
                      >
                        {template.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Logo Settings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-bold mb-3 lg:mb-4 text-gray-900">
                  Logo & Branding
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                      Logo Variant
                    </label>
                    <select
                      value={logoVariant}
                      onChange={e =>
                        setLogoVariant(
                          e.target.value as 'full' | 'mini' | 'full-plus'
                        )
                      }
                      className="w-full px-2.5 lg:px-3 py-1.5 lg:py-2 text-sm lg:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="full">Full Logo</option>
                      <option value="mini">Mini Logo (Icon Only)</option>
                      <option value="full-plus">Full Logo Plus</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                      Logo Position
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setLogoPosition('top')}
                        className={`flex-1 px-4 py-2 rounded-lg border transition-all ${
                          logoPosition === 'top'
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        Top
                      </button>
                      <button
                        onClick={() => setLogoPosition('bottom')}
                        className={`flex-1 px-4 py-2 rounded-lg border transition-all ${
                          logoPosition === 'bottom'
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        Bottom
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                      Logo Size: {logoSize}px
                    </label>
                    <input
                      type="range"
                      min="50"
                      max="250"
                      value={logoSize}
                      onChange={e => setLogoSize(parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                      Logo Grayscale: {logoGrayscale}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={logoGrayscale}
                      onChange={e => setLogoGrayscale(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      0% = full color, 100% = white logo
                    </p>
                  </div>

                  <div className="pt-2 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Logo Section Opacity
                    </h4>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Overall Opacity:{' '}
                          {(logoSectionOpacity * 100).toFixed(0)}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={logoSectionOpacity}
                          onChange={e =>
                            setLogoSectionOpacity(parseFloat(e.target.value))
                          }
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Controls opacity of logo + contact info together
                        </p>
                      </div>

                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-gray-700 mb-2">
                          <strong>Drag Logo:</strong> Click and drag on the
                          canvas to reposition
                        </p>
                        <button
                          onClick={resetLogoPosition}
                          className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                        >
                          Reset Position
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-900">
                        Contact Information
                      </h4>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-gray-600">Show</span>
                        <input
                          type="checkbox"
                          checked={showContactInfo}
                          onChange={e => setShowContactInfo(e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                      </label>
                    </div>

                    {showContactInfo && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Font Size: {contactFontSize}px
                          </label>
                          <input
                            type="range"
                            min="18"
                            max="60"
                            value={contactFontSize}
                            onChange={e =>
                              setContactFontSize(parseInt(e.target.value))
                            }
                            className="w-full"
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Text Opacity: {(contactOpacity * 100).toFixed(0)}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={contactOpacity}
                            onChange={e =>
                              setContactOpacity(parseFloat(e.target.value))
                            }
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Relative to section opacity
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Custom Color (optional)
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={contactColor || '#000000'}
                              onChange={e => setContactColor(e.target.value)}
                              className="w-16 h-10 rounded cursor-pointer"
                            />
                            {contactColor && (
                              <button
                                onClick={() => setContactColor('')}
                                className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Leave empty to use template color
                          </p>
                        </div>

                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Drag Contact Info:</strong> Click and drag
                            on the canvas to reposition independently
                          </p>
                          <button
                            onClick={resetContactPosition}
                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                          >
                            Reset Position
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Quote Mark Settings (only show for quote type) */}
            {contentType === 'quote' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.35 }}
              >
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg lg:text-xl font-bold mb-3 lg:mb-4 text-gray-900">
                    Quote Mark
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">
                        Show Quote Mark
                      </label>
                      <input
                        type="checkbox"
                        checked={showQuoteMark}
                        onChange={e => setShowQuoteMark(e.target.checked)}
                        className="w-5 h-5 rounded"
                      />
                    </div>

                    {showQuoteMark && (
                      <>
                        <div>
                          <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                            Quote Mark Style
                          </label>
                          <div className="grid grid-cols-3 gap-2 lg:gap-3">
                            <button
                              onClick={() => setQuoteMarkStyle('decorative-1')}
                              className={`p-2.5 lg:p-3 rounded-lg border-2 transition-all ${
                                quoteMarkStyle === 'decorative-1'
                                  ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              <span className="text-xs lg:text-sm font-medium">
                                Style 1
                              </span>
                            </button>
                            <button
                              onClick={() => setQuoteMarkStyle('decorative-2')}
                              className={`p-2.5 lg:p-3 rounded-lg border-2 transition-all ${
                                quoteMarkStyle === 'decorative-2'
                                  ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              <span className="text-xs lg:text-sm font-medium">
                                Style 2
                              </span>
                            </button>
                            <button
                              onClick={() => setQuoteMarkStyle('decorative-3')}
                              className={`p-2.5 lg:p-3 rounded-lg border-2 transition-all ${
                                quoteMarkStyle === 'decorative-3'
                                  ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              <span className="text-xs font-medium">
                                Style 3
                              </span>
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Choose your preferred quotation mark style
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Quote Mark Size: {quoteMarkSize}px
                          </label>
                          <input
                            type="range"
                            min="100"
                            max="400"
                            value={quoteMarkSize}
                            onChange={e =>
                              setQuoteMarkSize(parseInt(e.target.value))
                            }
                            className="w-full"
                          />
                        </div>

                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Drag Quote Mark:</strong> Click and drag on
                            the canvas to reposition
                          </p>
                          <button
                            onClick={resetQuotePosition}
                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                          >
                            Reset Position
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StatusImageCreator;
